import i18next from "i18next";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FIELD_REQUIRED, LOADING } from "../../../../i18n/keysTranslations";
import { Controller } from "react-hook-form";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { useTranslationApp } from "../../../../lib/i18next";
import { getReactHookFormNestedError } from "../../../../utils/errors";
import { useDispatchApp } from "../../../../lib/redux";
import { startGetCatalogsAdsCampaigns } from "../../../../actions/adsCampaigns";
import { TIKTOK } from "../../../../utils/constants";
import _ from "lodash";
import { INPUT_COLOR_BORDER } from "../../../../utils/colors";

const CATALOGS_PAGE_SIZE = 100;

const SelectCatalogTikTok = ({
  control,
  errors,
  rules = {
    required: {
      value: true,
      message: i18next.t(FIELD_REQUIRED),
    },
  },
  name,
  label,
  disabled,
  integrationID,
  adAccountID,
  catalogID,
  fullWidth,
  variant = "filled",
  placeholder,
  helperText,
  onChangeAction = () => {},
  disableClearable = false,
  sxTextField = {},
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const loadingMoreRef = useRef(false);
  const error = getReactHookFormNestedError(errors, name);
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();

  const onGetCatalogsAdsCampaigns = useCallback(
    async (lastKey = 1, append = false) => {
      if (!integrationID || !adAccountID) return;
      if (append) {
        loadingMoreRef.current = true;
      } else {
        setLoading(true);
      }
      const response = await dispatch(
        startGetCatalogsAdsCampaigns({
          integrationID,
          adAccountID,
          includedID: catalogID,
          lastKey,
          platform: TIKTOK,
        })
      );
      if (response.ok) {
        const data = response.data;
        const items = Array.isArray(data) ? data : (data?.data ?? []);
        const hasMoreData = data?.hasMore ?? items.length >= CATALOGS_PAGE_SIZE;
        setHasMore(hasMoreData);
        if (append) {
          setOptions((prev) => _.uniqBy([...prev, ...items], "id"));
        } else {
          setOptions(items?.length > 0 ? items : []);
        }
      }
      if (append) {
        loadingMoreRef.current = false;
      } else {
        setLoading(false);
      }
    },
    [integrationID, adAccountID, catalogID, dispatch]
  );

  const onLoadMore = useCallback(() => {
    if (!loadingMoreRef.current && hasMore && !loading) {
      const nextPage = page + 1;
      setPage(nextPage);
      onGetCatalogsAdsCampaigns(nextPage, true);
    }
  }, [hasMore, loading, page, onGetCatalogsAdsCampaigns]);

  const onScrollListbox = useCallback(
    (event) => {
      const listboxNode = event.currentTarget;
      const scrollBottom =
        listboxNode.scrollHeight -
        listboxNode.scrollTop -
        listboxNode.clientHeight;
      if (scrollBottom < 50 && hasMore && !loading && !loadingMoreRef.current) {
        onLoadMore();
      }
    },
    [hasMore, loading, onLoadMore]
  );

  useEffect(() => {
    if (integrationID && adAccountID) {
      setPage(1);
      setHasMore(true);
      onGetCatalogsAdsCampaigns(1, false);
    }
    // eslint-disable-next-line
  }, [integrationID, adAccountID]);

  return (
    <Controller
      defaultValue={""}
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => (
        <Autocomplete
          {...field}
          hasLabel={Boolean(label)}
          fullWidth={fullWidth}
          disabled={disabled}
          disableClearable={disableClearable}
          filterOptions={(x) => x}
          loadingText={
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CircularProgress color="inherit" size={20} />
              {t(LOADING)}
            </Box>
          }
          loading={loading}
          open={open}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          onChange={(_, value) => {
            if (value?.id) {
              field.onChange(value.id);
              onChangeAction(value);
            } else {
              field.onChange(null);
              onChangeAction(null);
            }
          }}
          isOptionEqualToValue={(option, value) => option?.id === value}
          getOptionLabel={(option) => {
            const optionValue = options.find((opt) => opt.id === option);
            if (optionValue) {
              return `${optionValue?.name} - ${optionValue?.id}`;
            }
            if (typeof option === "string") {
              return option;
            }
            return `${option?.name} - ${option?.id}`;
          }}
          options={options}
          ListboxProps={{
            onScroll: onScrollListbox,
          }}
          renderOption={(props, option) => {
            return (
              <Box
                {...props}
                component="li"
                key={option.id}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  cursor: "pointer",
                }}
              >
                <Typography variant="body2">
                  {option.name} - {option.id}
                </Typography>
              </Box>
            );
          }}
          renderInput={(params) => (
            <TextField
              id={name}
              {...params}
              label={label}
              variant={variant}
              hiddenLabel={!label}
              placeholder={field?.value?.length > 0 ? "" : placeholder}
              error={!!error}
              helperText={
                <Box
                  component="span"
                  sx={{ display: "flex", justifyContent: "space-between" }}
                >
                  <span>{!!error ? error?.message : helperText}</span>
                </Box>
              }
              sx={{
                backgroundColor: "fields.background",
                border: `1px solid ${INPUT_COLOR_BORDER}`,
                boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                ...sxTextField,
                ".MuiFormHelperText-root": {
                  ml: "0 !important",
                },
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
        />
      )}
    />
  );
};

export default SelectCatalogTikTok;
